import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { Client,
  CommonClientService,
  CommonSectorService,
  CommonSiteService,
  MQTT_KEYS,
  MQTT_TOPICS,
  mqttSyncSubscriptions,
  RootState,
  Sector,
  Site,
  useMqttSubscription,
  usePersistantState,
  UUID,
  JSONObject } from '@umanforge/common';
import { TableCreation } from 'interfaces/types.interface';

import { EventManagerService } from 'services/eventManagers.service';
import { GlobalService } from 'services/global.service';
import { ToastService } from 'services/toast.service';

import EventManagerModal from 'components/modals/eventManagers/eventManagerModal.component';
import BottomBar from '@common_components/layout/bottomBar.component';
import HomeTopBar from '../@common_components/global/homeTopBar.component';
import HomeLeftBar from '@common_components/layout/LeftBarManager';
import Tabs from '@common_components/buttons/Tabs.component';
import { Loading } from '@common_components/loading.component';
import HomeContent from 'components/common/HomeContent.component';
import { KEY } from 'constants/local_storage_key.constant';
import ClientSelector from '@common_components/layout/clientSelector.component';
import DataChecker from 'components/common/dataChecker';
import { EventManagerWithPosition } from 'interfaces/eventManager.interface';
import ConfirmationModal from '@common_components/modals/confirmationModal.component';

const toastService: ToastService = new ToastService( 'Home' );

function Home() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const token = useSelector( ( state: RootState ) => {return state.token.value;} );

  const popupRef = useRef( null );
  const buttonRef = useRef( null );

  const [ clients, setClients ] = useState<Client[]>( [] );
  const [ selectedClientId, setSelectedClientId ] = usePersistantState<UUID | undefined>( KEY.SELECTED_CLIENT_ID, undefined );

  const [ sites, setSites ] = useState<Site[]>( [] );
  const [ selectedSiteId, setSelectedSiteId ] = usePersistantState<UUID | undefined>( KEY.SELECTED_SITE_ID, undefined );

  const [ sectors, setSectors ] = useState<Sector[]>( [] );
  const [ selectedSectorId, setSelectedSectorId ] = usePersistantState<UUID | undefined>( KEY.SELECTED_SECTOR_ID, undefined );

  const [ tables, setTables ] = useState<EventManagerWithPosition[]>( [] );

  const [ , setSelectedEventManagerId ] = usePersistantState<UUID | undefined>( KEY.SELECTED_EVENT_MANAGER_ID, undefined );
  const [ isClientSelectorPopupVisible, setClientSelectorPopupVisible ] = useState( false );
  const [ isEventManagerModalVisible, setEventManagerModalVisible ] = useState( false );

  const [ isLoading, setIsLoading ] = useState( true );
  const [ isFirstRender, setIsFirstRender ] = useState( true );

  const [ isDragModeActive, setIsDragModeActive ] = useState( false );
  const [ showConfirmationModal, setShowConfirmationModal ] = useState( false );
  const [ pendingSiteIndex, setPendingSiteIndex ] = useState<number | null>( null );

  useEffect( () => {
    setIsFirstRender( false );
  }, [] );

  useEffect( () => {
    if ( !isFirstRender ) {
      inits();
    }
  }, [ isFirstRender, selectedSectorId  ] );

  useEffect( () => {
    if ( !isFirstRender ) {
      onClientsChange();
    }
  }, [ clients ] );

  useEffect( () => {
    if ( !isFirstRender && selectedClientId && GlobalService.isValidGuid( selectedClientId ) ) {
      refreshSites();
    } else if ( !isFirstRender && !selectedClientId ) {
      setSites( [] );
    }
  }, [ selectedClientId ] );

  useEffect( () => {
    if ( !isFirstRender ) {
      onSitesChange();
    }
  }, [ sites ] );

  useEffect( () => {
    if ( !isFirstRender && selectedSiteId && GlobalService.isValidGuid( selectedSiteId ) ) {
      refreshSectors();
    } else if ( !isFirstRender && !selectedSiteId ) {
      setSectors( [] );
    }
  }, [ selectedSiteId ] );

  useEffect( () => {
    if ( !isFirstRender ) {
      onSectorsChange();
    }
  }, [ sectors ] );

  useEffect( () => {
    if ( !isFirstRender ) {
      refreshEventManagers( selectedSectorId );
    }
    const handleVisibilityChange = () => {
      if ( !document.hidden && GlobalService.isValidGuid( selectedSectorId ) ) {
        refreshEventManagers( selectedSectorId );
      }
    };

    document.addEventListener( 'visibilitychange', handleVisibilityChange );

    return () => {
      document.removeEventListener( 'visibilitychange', handleVisibilityChange );
    };
  }, [ selectedSectorId ] );

  const selectedClient = useMemo( () => {
    return clients.find( c => { return c.id === selectedClientId; } ) || null;
  }, [ clients, selectedClientId ] );

  const remainingClients = useMemo( () => {
    return clients.filter( c => { return c.id !== selectedClientId; } );
  }, [ clients, selectedClientId ] );

  const toggleClientSelectorPopup = useCallback( () => {
    if ( clients.length > 1 ) {
      setClientSelectorPopupVisible( prev => { return !prev; } );
    }
  }, [ clients ] );

  const openEventManagerModal = useCallback( () => {
    setEventManagerModalVisible( true );
  }, [] );

  //--------------- Functions ---------------\\
  const inits = async () => {
    await refreshClients();
    await refreshSites();
    await refreshSectors();
    setIsLoading( false );
  };

  const refreshClients = async () => {
    const clientsFetched: Client[] = await CommonClientService.getAllowedClients( null, token, dispatch );
    mqttSyncSubscriptions( clients, clientsFetched, MQTT_TOPICS.umClient );
    setClients( clientsFetched );
  };

  const refreshSites = async () => {
    const sitesFetched: Site[] = [];
    if ( selectedClientId && GlobalService.isValidGuid( selectedClientId ) ) {
      sitesFetched.push( ...( await CommonSiteService.getAllowedSitesByClientsId( [ selectedClientId ], token, dispatch ) ) );
    }

    mqttSyncSubscriptions( sites, sitesFetched, MQTT_TOPICS.umSite );
    setSites( sitesFetched );
  };

  const refreshSectors = async () => {
    const sectorsFetched: Sector[] = [];
    if ( selectedSiteId && GlobalService.isValidGuid( selectedSiteId ) ) {
      sectorsFetched.push( ...( await CommonSectorService.getAllowedSectorsBySitesId( [ selectedSiteId ], token, dispatch ) ) );
    }

    mqttSyncSubscriptions( sectors, sectorsFetched, MQTT_TOPICS.umSector );
    setSectors( sectorsFetched );
  };

  useMqttSubscription( ( msg: JSONObject, category: string | null ) => {
    if ( category === 'client' ) {
      refreshClients();
    }
    if ( category === 'site' ) {
      refreshSites();
    }
    if ( category === 'sector' ) {
      if ( msg.key && msg.key === MQTT_KEYS.FM_EVENT_MANAGER ) {
        refreshEventManagers( selectedSectorId );
      } else {
        refreshSectors();
      }
    }
  } );

  const goToTableManagement = ( tableName: string, tableId: string ) => {
    setSelectedEventManagerId( tableId );
    navigate( '/EventManager', { state: { sites } } );
  };

  const refreshEventManagers = async ( sectorId: string | undefined ) => {
    const fetchedTables = [];
    if ( sectorId && GlobalService.isValidGuid( sectorId ) ) {
      const tablesData = await EventManagerService.getTablesBySector( sectorId, token, dispatch );
      if ( tablesData && Array.isArray( tablesData ) ) {
        fetchedTables.push( ...tablesData );
      }
    }
    setTables( fetchedTables );
  };

  const getSafeSelectedSectorId = (): string | undefined => {
    return GlobalService.isValidGuid( selectedSectorId ) ? selectedSectorId : undefined;
  };

  const getSelectedSiteIndex = () => {
    if ( !selectedSiteId || sites.length === 0 ) {
      return -1;
    }
    return sites.findIndex( site => {return site.id === selectedSiteId;} );
  };

  //--------------- Events ---------------\\
  const onClientsChange = () => {
    if ( clients && clients.length > 0 ) {
      if ( !clients.some( client => { return client.id === selectedClientId; } ) ) {
        setSelectedClientId( clients[ 0 ]?.id );
      }
    } else {
      setSelectedClientId( undefined );
    }
  };

  const onSitesChange = () => {
    if ( sites && sites.length > 0 ) {
      if ( !sites.some( site => { return site.id === selectedSiteId; } ) ) {
        setSelectedSiteId( sites[ 0 ]?.id );
      }
    } else {
      setSelectedSiteId( undefined );
    }
  };

  const onSectorsChange = () => {
    if ( sectors && sectors.length > 0 ) {
      if ( !sectors.some( sector => { return sector.id === selectedSectorId; } ) ) {
        const firstSectorId = sectors[ 0 ]?.id;
        setSelectedSectorId( firstSectorId );
      }
    } else {
      setSelectedSectorId( undefined );
    }
  };

  const handleCloseEventManagerModal = () => {
    setEventManagerModalVisible( false );
  };

  const handleCreateTable = async ( table: TableCreation ): Promise<UUID | null> => {
    try {
      const newTableId = await EventManagerService.createTable( table, token, dispatch );
      if ( newTableId ) {
        setEventManagerModalVisible( false );

        const safeSectorId = getSafeSelectedSectorId();
        if ( safeSectorId ) {
          setTimeout( async () => {
            await refreshEventManagers( safeSectorId );
          }, 100 );
        }

        toastService.showSuccess( 'translation:event_manager_created' );
        return newTableId;
      } else {
        toastService.showError( 'translation:event_manager_creation_failed' );
        return null;
      }
    } catch ( error ) {
      console.error( 'Error creating table:', error );
      toastService.showError( 'translation:event_manager_creation_error' );
      return null;
    }
  };

  const handleClientClick = ( clientIndex: number ) => {
    if ( clients && clients.length > clientIndex && clientIndex >= 0 ) {
      setSelectedClientId( clients[ clientIndex ]?.id );
    }
  };

  const handleSiteClick = ( siteIndex: number ) => {
    if ( isDragModeActive ) {
      setPendingSiteIndex( siteIndex );
      setShowConfirmationModal( true );
    } else {
      performSiteClick( siteIndex );
    }
  };

  const performSiteClick = ( siteIndex: number ) => {
    if ( sites && sites.length > siteIndex && siteIndex >= 0 ) {
      setSelectedSiteId( sites[ siteIndex ]?.id );
    }
  };

  const handleConfirmSiteChange = () => {
    if ( pendingSiteIndex !== null ) {
      setIsDragModeActive( false );
      performSiteClick( pendingSiteIndex );
    }
    setShowConfirmationModal( false );
    setPendingSiteIndex( null );
  };

  const handleCancelSiteChange = () => {
    setShowConfirmationModal( false );
    setPendingSiteIndex( null );
  };

  const handleSectorClick = ( sectorIndex: number ) => {
    if ( sectors && sectors.length > sectorIndex && sectorIndex >= 0 ) {
      const sectorId = sectors[ sectorIndex ]?.id;
      if ( GlobalService.isValidGuid( sectorId ) ) {
        setSelectedSectorId( sectorId );
      }
    }
  };

  //--------------- Front ---------------\\
  const clientForSelector = selectedClient || clients[ 0 ];
  return (
    <>
      { token ? (
        <div className="flex min-h-screen w-full bg-white-base text-black relative dark:bg-dark-bg dark:text-white trans-fast">
          <div className="flex w-full pb-48">
            <HomeLeftBar variant="basic" />
            <div className="w-full px-4 max-md:pr-4 h-full">
              {
                isLoading ?
                  <div className='flex h-full items-center justify-center'>
                    <Loading size={ 100 }/>
                  </div>
                  :
                  <>
                    <HomeTopBar>
                      <ClientSelector
                        selected={clientForSelector}
                        otherClients={remainingClients}
                        toggle={toggleClientSelectorPopup}
                        showPopup={isClientSelectorPopupVisible}
                        onClientClick={ client => {
                          const idx = clients.findIndex( c => {return c.id === client.id;} );
                          if ( idx !== -1 ) {
                            handleClientClick( idx );
                          }
                        } }
                        popupRef={popupRef}
                        buttonRef={buttonRef}
                        isClickable={clients.length > 1}
                      />
                    </HomeTopBar>
                    <Tabs
                      items={sites}
                      activeItem={getSelectedSiteIndex()}
                      onItemClick={handleSiteClick}
                    />
                    <DataChecker clients={clients} sites={sites} sectors={sectors}>
                      <HomeContent
                        sectors={sectors}
                        tables={tables}
                        selectedSectorId={getSafeSelectedSectorId()}
                        setTables={setTables}
                        goToTableManagement={goToTableManagement}
                        setPopupVisible={openEventManagerModal}
                        handleSectorClick={handleSectorClick}
                        onDragModeChange={setIsDragModeActive}
                      />
                    </DataChecker >
                  </>
              }
            </div>
            <BottomBar />
          </div>

          { isEventManagerModalVisible && (
            <EventManagerModal
              onClose={handleCloseEventManagerModal}
              onSubmit={handleCreateTable}
            />
          ) }

          { showConfirmationModal && (
            <ConfirmationModal
              title={t( 'translation:confirm_exit_drag_mode' )}
              description={t( 'translation:confirm_exit_drag_mode_description' )}
              isOpen={showConfirmationModal}
              onConfirm={handleConfirmSiteChange}
              onCancel={handleCancelSiteChange}
            />
          ) }
        </div>
      ) : (
        <Loading />
      )}
    </>
  );
}

export default Home;
